import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import { MOCK_DATA_BY_IDENTITY_TYPE } from '../components/Details/FUEInventory/FUEInventoryTable/constants/mockFueInventoryData';
import {
  getAccountsForIdentity,
  getRolesForAccount,
  getRolesForIdentity,
} from '../mock-data/sharedLicenseDummyData';

/** Transformed result used in the UI */
export interface VendorEndpoint {
  id: string;
  endpointname: string;
  displayName: string;
}

/** FUE Inventory KPI summary response */
export interface FueInventoryKpiData {
  purchasedUnit: number;
  allocatedUnit: number;
  dormantUsers: {
    count: number;
    unit: number;
  };
  downgradeCandidates?: number | { count: number; hasSimulation?: boolean };
  potentialSavings?: {
    savingsUnit: number;
    cost?: number;
    dollars?: number;
    displayCost?: string;
    displayValue?: string;
    hasSimulation?: boolean;
  };
}

/** Query params for FUE inventory KPI endpoint */
interface FueInventoryKpiQueryParams {
  vendor: string;
  endpointKey?: number;
  cloudLicenseKey?: number | null;
}

/** Filter option item returned by the inventory filters endpoint */
export interface FilterOption {
  value: string;
  label: string;
}

/** Transformed response from the inventory filters endpoint */
export interface InventoryFiltersData {
  requestType: string;
  filters: {
    identityTypes: FilterOption[];
    tiers: FilterOption[];
    statuses: FilterOption[];
    departments: FilterOption[];
  };
}

/** Vendor query params shared by several endpoints */
interface VendorQueryParams {
  vendor: string;
  cloudLicenseKey?: number | null;
}

/** Per-identity tier/unit breakdown returned inside each search result */
export interface IdentityUnitAnalysis {
  currentTier: string;
  currentUnit: number;
  recommendedTier: string;
  recommendedUnit: number;
  savingsUnit: number;
  savingsCost: number;
}

/** One row returned by the identity search endpoint */
export interface IdentitySearchRow {
  identityId: string;
  accountKey?: number;
  /** Human: "username (First Last)" format. */
  displayName?: string;
  email?: string;
  /** Non-human identity types: the account name used as the Job ID. */
  accountName?: string;
  identityType: string;
  currentTier: string;
  currentUnit?: number;
  recommendedTier: string;
  recommendedUnit?: number;
  evaluatedCost: number;
  evaluationStatus: string;
  departmentName: string;
  lastLoginAt?: string;
  lastActiveAt: string;
  daysInactive?: number;
  unitAnalysis?: IdentityUnitAnalysis;
  recommendationReason?: string;
}

/** Transformed response from the identity search endpoint */
export interface IdentitySearchData {
  results: IdentitySearchRow[];
  searchAfter: string[] | null;
  pageSize: number;
  displayRecords: number;
  totalRecords: number;
}

/** Request params for the identity search endpoint */
interface IdentitySearchParams {
  vendor: string;
  identityType: string;
  /** Already-stringified filter values — each key maps to a comma-joined string */
  filters: Record<string, string[]>;
  searchAfter?: string[] | null;
  cloudLicenseKey?: number | null;
  searchText?: string | null;
}

/** One account row returned by the identity accounts search endpoint */
export interface IdentityAccountRow {
  accountKey: number;
  accountName: string;
  endpointKey: number;
  endpointName: string;
  highestTierWeight: number;
  currentTier: string;
  status: string;
}

/** Transformed response from the identity accounts search endpoint */
export interface IdentityAccountSearchData {
  identityId: string;
  results: IdentityAccountRow[];
  searchAfter: string[] | null;
  totalRecords: number;
  pageSize: number;
}

/** Request params for the identity accounts search endpoint */
interface IdentityAccountSearchParams {
  vendor: string;
  identityId: string;
  identityType: string;
  cloudLicenseKey?: number | null;
  searchAfter?: string[] | null;
  pageSize?: number;
  searchText?: string | null;
}

/** A child role inside a composite role */
export interface AccountRoleChildRole {
  roleKey: number;
  roleName: string;
  tierWeight: number;
}

/** One role entry returned by the account roles search endpoint */
export interface AccountRole {
  roleKey: number;
  roleName: string;
  roleType: string;
  tierWeight: number;
  entitlementUsed: boolean;
  childRoles: AccountRoleChildRole[];
}

/** Transformed response from the account roles search endpoint */
export interface AccountRoleSearchData {
  accountKey: number;
  roles: AccountRole[];
  searchAfter: string[] | null;
  totalRecords: number;
  pageSize: number;
}

/** Request params for the account roles search endpoint */
interface AccountRoleSearchParams {
  vendor: string;
  accountKey: number;
  cloudLicenseKey?: number | null;
  searchAfter?: string[] | null;
  pageSize?: number;
}

/** Transformed response from the identity roles search endpoint */
export interface IdentityRoleSearchData {
  identityId: string;
  roles: AccountRole[];
  totalRecords: number;
}

/** Request params for the identity roles search endpoint */
interface IdentityRoleSearchParams {
  vendor: string;
  identityId: string;
  identityType: string;
  cloudLicenseKey?: number | null;
  searchText?: string | null;
}

/** One field/value pair within an auth object */
export interface RoleAuthObjectField {
  field: string;
  value: string;
}

/** One auth object entry returned by the role auth objects search endpoint */
export interface RoleAuthObject {
  authObjectKey: number;
  authObjectName: string;
  fields: RoleAuthObjectField[];
  fueScore?: number;
}

/** Transformed response from the role auth objects search endpoint */
export interface RoleAuthObjectSearchData {
  authObjects: RoleAuthObject[];
  searchAfter: string[] | null;
  pageSize: number;
  totalRecords: number;
}

/** Request params for the role auth objects search endpoint */
interface RoleAuthObjectSearchParams {
  vendor: string;
  roleKey: number;
  cloudLicenseKey?: number | null;
  searchAfter?: string[] | null;
  pageSize?: number;
}

export const licenseManagementApi = createApi({
  reducerPath: 'licenseManagementApi',
  baseQuery: axiosBaseQuery({}),
  keepUnusedDataFor: 300,
  endpoints: () => ({}),
});

export const licenseManagementApiEndpoints =
  licenseManagementApi.injectEndpoints({
    endpoints: (builder) => ({
      // Disabled — using local mock data instead
      // getSapSnapshot: builder.query<SapSnapshotData, string>({
      //   query: (vendor) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/snapshot`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: SapSnapshotResponse) => response.data,
      // }),

      // Disabled — using local mock data instead
      // getUnitConsumption: builder.query<UnitConsumptionData, string>({
      //   query: (vendor) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/overview/unit-consumption`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: { data: UnitConsumptionData }) =>
      //     response.data,
      // }),

      // Disabled — using local mock data instead
      // getLicenceInventory: builder.query<LicenseInventoryApiData, string>({
      //   query: (vendor) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/overview/licence-inventory`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: LicenseInventoryApiResponse) =>
      //     response.data,
      // }),

      // Disabled — using local mock data instead
      // getOptimizationFindings: builder.query<FindingsData, FindingsQueryParams>(
      //   {
      //     query: ({ vendor, page = 0, size = 10 }) => ({
      //       url: `/api/sodms/license-intelligence/v1/findings?scope=${vendor}&page=${page}&size=${size}`,
      //       method: 'GET',
      //       isECMv6: true,
      //     }),
      //     transformResponse: (response: FindingsApiResponse) => response.data,
      //   }
      // ),

      // Disabled — using local mock data instead
      // getUnitConsumptionTrend: builder.query<UnitConsumptionTrendData, string>({
      //   query: (vendor) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/overview/unit-consumption-trend`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: { data: UnitConsumptionTrendData }) =>
      //     response.data,
      // }),

      // Disabled — using local mock data instead
      // getIdentityDistribution: builder.query<
      //   IdentityTypeDistributionData,
      //   string
      // >({
      //   query: (vendor) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/overview/identity-distribution`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: { data: IdentityTypeDistributionData }) =>
      //     response.data,
      // }),

      // Disabled — using local mock data instead
      // getFueInventoryKpi: builder.query<
      //   FueInventoryKpiData,
      //   FueInventoryKpiQueryParams
      // >({
      //   query: ({ vendor, endpointKey }) => ({
      //     url: `/api/sodms/license-intelligence/v1/${vendor}/licence-inventory${
      //       endpointKey ? `?endpointKey=${endpointKey}` : ''
      //     }`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: { data: FueInventoryKpiData }) =>
      //     response.data,
      // }),

      getLicenseStatus: builder.query<{ showAddLicenseModal: boolean }, string>(
        {
          queryFn: () => {
            // Read from localStorage so the flag can be toggled from the browser console:
            // localStorage.setItem('licenseIntelligence_showAddLicenseModal', 'true')
            const stored = localStorage.getItem(
              'licenseIntelligence_showAddLicenseModal'
            );
            const showAddLicenseModal =
              stored !== null ? stored === 'true' : false;

            return { data: { showAddLicenseModal } };
          },
        }
      ),

      // Dummy data — FUE inventory KPI summary.
      getFueInventoryKpi: builder.query<
        FueInventoryKpiData,
        FueInventoryKpiQueryParams
      >({
        queryFn: () => ({
          data: {
            purchasedUnit: 4200,
            allocatedUnit: 3847,
            dormantUsers: { count: 10, unit: 7.6 },
            downgradeCandidates: { count: 20, hasSimulation: true },
            potentialSavings: {
              savingsUnit: 13.6,
              dollars: 13056,
              displayCost: '~$13K',
              displayValue: '~$13K',
              hasSimulation: true,
            },
          },
        }),
      }),

      // Dummy data — inventory filter options (drives identity-type tabs + filters).
      getInventoryFilters: builder.query<
        InventoryFiltersData,
        VendorQueryParams
      >({
        queryFn: () => ({
          data: {
            requestType: 'licenseInventory',
            filters: {
              identityTypes: [
                { value: 'Human', label: 'Human' },
                { value: 'Service Account', label: 'Service Account' },
                { value: 'Emergency', label: 'Emergency' },
                { value: 'Communication', label: 'Communication' },
                { value: 'Orphan', label: 'Orphan' },
                { value: 'AI Agent', label: 'AI Agent' },
                { value: 'Batch', label: 'Batch' },
              ],
              tiers: [
                { value: 'Advanced User', label: 'Advanced User' },
                { value: 'Core User', label: 'Core User' },
                { value: 'Self-Service User', label: 'Self-Service User' },
                { value: 'Developer User', label: 'Developer User' },
              ],
              statuses: [
                { value: 'Compliant', label: 'Compliant' },
                { value: 'Over-licensed', label: 'Over-licensed' },
                { value: 'Dormant', label: 'Dormant' },
              ],
              departments: [
                { value: 'Engineering', label: 'Engineering' },
                { value: 'Finance', label: 'Finance' },
                { value: 'HR', label: 'HR' },
                { value: 'Sales', label: 'Sales' },
                { value: 'Marketing', label: 'Marketing' },
                { value: 'Legal', label: 'Legal' },
                { value: 'Operations', label: 'Operations' },
                { value: 'IT', label: 'IT' },
                { value: 'Product', label: 'Product' },
                { value: 'Security', label: 'Security' },
              ],
            },
          },
        }),
      }),

      // Dummy data — identity search (table rows), sourced from local mock data.
      // Applies identityType selection, free-text search, and filter values, then
      // paginates using a cursor (searchAfter holds the next start index).
      searchIdentities: builder.query<IdentitySearchData, IdentitySearchParams>(
        {
          queryFn: ({
            identityType,
            filters = {},
            searchAfter = null,
            searchText = null,
          }) => {
            const source = MOCK_DATA_BY_IDENTITY_TYPE[identityType] ?? [];

            // Apply free-text search against the display name / account name / email.
            const text = (searchText ?? '').trim().toLowerCase();
            const textFiltered = text
              ? source.filter((row) =>
                  [row.displayName, row.accountName, row.email]
                    .filter((v): v is string => Boolean(v))
                    .some((v) => v.toLowerCase().includes(text))
                )
              : source;

            // Apply structured filters (tiers, statuses, departments) when present.
            const matchesFilter = (
              values: string[] | undefined,
              candidate: string
            ) => !values || values.length === 0 || values.includes(candidate);

            const filtered = textFiltered.filter(
              (row) =>
                matchesFilter(filters.tiers, row.currentTier) &&
                matchesFilter(filters.statuses, row.evaluationStatus) &&
                matchesFilter(filters.departments, row.departmentName)
            );

            const pageSize = 20;
            const start = searchAfter ? Number(searchAfter[0]) : 0;
            const end = Math.min(start + pageSize, filtered.length);
            const results = filtered.slice(start, end);
            const nextCursor = end < filtered.length ? [String(end)] : null;

            return {
              data: {
                results,
                searchAfter: nextCursor,
                pageSize,
                displayRecords: filtered.length,
                totalRecords: filtered.length,
              },
            };
          },
        }
      ),

      // Dummy data — accounts for a given identity, derived from the shared
      // dummy-data module so account names are unique and consistent.
      searchIdentityAccounts: builder.query<
        IdentityAccountSearchData,
        IdentityAccountSearchParams
      >({
        queryFn: ({
          identityId,
          searchAfter = null,
          searchText = null,
          pageSize = 50,
        }) => {
          const all = getAccountsForIdentity(identityId);

          const text = (searchText ?? '').trim().toLowerCase();
          const filtered = text
            ? all.filter(
                (a) =>
                  a.accountName.toLowerCase().includes(text) ||
                  a.endpointName.toLowerCase().includes(text)
              )
            : all;

          const start = searchAfter ? Number(searchAfter[0]) : 0;
          const end = Math.min(start + pageSize, filtered.length);
          const results = filtered.slice(start, end);
          const nextCursor = end < filtered.length ? [String(end)] : null;

          return {
            data: {
              identityId,
              results,
              searchAfter: nextCursor,
              totalRecords: filtered.length,
              pageSize,
            },
          };
        },
      }),

      // Dummy mutation — simulates starting a license simulation run.
      runSimulation: builder.mutation<
        { message: string },
        {
          cloudLicenseKey: number;
          rulesetKey: number;
          vendorType: string;
        }
      >({
        queryFn: () =>
          new Promise<{ data: { message: string } }>((resolve) => {
            setTimeout(() => {
              resolve({ data: { message: 'Simulation started' } });
            }, 800);
          }),
      }),

      // Dummy query — returns an empty blob to stand in for a report download.
      downloadLicenseExport: builder.query<Blob, string>({
        queryFn: () => ({
          data: new Blob(['dummy export'], { type: 'text/plain' }),
        }),
      }),

      // Dummy data — ruleset options for the Run Simulation modal.
      getRulesets: builder.query<
        { label: string; value: string }[],
        { vendorType: string }
      >({
        queryFn: () => ({
          data: [
            { label: 'License Ruleset v3', value: '3' },
            { label: 'License Ruleset v2', value: '2' },
            { label: 'License Ruleset v1', value: '1' },
          ],
        }),
      }),

      // Dummy data — account roles derived from the shared dummy-data module so
      // role names stay unique and consistent across the app.
      searchAccountRoles: builder.query<
        AccountRoleSearchData,
        AccountRoleSearchParams
      >({
        queryFn: ({ accountKey, searchAfter = null, pageSize = 20 }) => {
          const all = getRolesForAccount(accountKey);
          const start = searchAfter ? Number(searchAfter[0]) : 0;
          const end = Math.min(start + pageSize, all.length);
          const roles = all.slice(start, end);
          const nextCursor = end < all.length ? [String(end)] : null;

          return {
            data: {
              accountKey,
              roles,
              searchAfter: nextCursor,
              totalRecords: all.length,
              pageSize,
            },
          };
        },
      }),

      // Dummy data — all roles directly assigned to an identity, independent of
      // account-level role lookups. Supports free-text search on role name.
      searchIdentityRoles: builder.query<
        IdentityRoleSearchData,
        IdentityRoleSearchParams
      >({
        queryFn: ({ identityId, searchText = null }) => {
          const all = getRolesForIdentity(identityId);
          const text = (searchText ?? '').trim().toLowerCase();
          const roles = text
            ? all.filter((r) => r.roleName.toLowerCase().includes(text))
            : all;

          return {
            data: {
              identityId,
              roles,
              totalRecords: roles.length,
            },
          };
        },
      }),

      // Dummy data — mimics the role auth objects search endpoint.
      // Returns paginated auth objects (each with 1–6 fields) using cursor pagination.
      searchRoleAuthObjects: builder.query<
        RoleAuthObjectSearchData,
        RoleAuthObjectSearchParams
      >({
        queryFn: ({ roleKey, searchAfter = null, pageSize = 20 }) => {
          const TOTAL = 60;
          const start = searchAfter ? Number(searchAfter[0]) : 0;
          const end = Math.min(start + pageSize, TOTAL);

          const authObjects: RoleAuthObject[] = Array.from(
            { length: Math.max(0, end - start) },
            (_unused, i) => {
              const idx = start + i;
              const fieldCount = (idx % 6) + 1;

              return {
                authObjectKey: roleKey * 100 + idx,
                authObjectName: `S_AUTH_OBJ_${idx}`,
                fueScore: (idx % 5) + 1,
                fields: Array.from({ length: fieldCount }, (_f, f) => ({
                  field: `FIELD_${idx}_${f}`,
                  value: `VALUE_${idx}_${f}`,
                })),
              };
            }
          );

          const nextCursor = end < TOTAL ? [String(end)] : null;

          return {
            data: {
              authObjects,
              searchAfter: nextCursor,
              totalRecords: TOTAL,
              pageSize,
            },
          };
        },
      }),

      // Disabled — using local mock data instead
      // getVendorEndpoints: builder.query<VendorEndpoint[], string>({
      //   query: (vendor) => ({
      //     url: '/api/v5/getEndpoints',
      //     method: 'POST',
      //     params: {
      //       max: 500,
      //       offset: 0,
      //       connectionType: vendor.toUpperCase(),
      //     },
      //   }),
      //   transformResponse: (response: GetVendorEndpointsResponse) =>
      //     (response.endpoints ?? []).map((ep) => ({
      //       id: ep.id ?? '',
      //       endpointname: ep.endpointname,
      //       displayName: ep.displayName || ep.endpointname,
      //     })),
      // }),

      // Disabled — using local mock data instead
      // getRulesets: builder.query<
      //   { label: string; value: string }[],
      //   { vendorType: string }
      // >({
      //   query: ({ vendorType }) => ({
      //     url: '/api/sodms/rulesets',
      //     method: 'GET',
      //     isECMv6: true,
      //     params: {
      //       includeDetails: true,
      //       max: 500,
      //       offset: 0,
      //       rulesetName: '',
      //       vendorType,
      //       rulesetType: 'LICENSE',
      //     },
      //   }),
      //   transformResponse: (response: {
      //     data?: { id: number; rulesetName: string }[];
      //     status?: number;
      //   }) => {
      //     const rulesets = Array.isArray(response.data) ? response.data : [];
      //     return rulesets.map((r) => ({
      //       label: r.rulesetName,
      //       value: String(r.id),
      //     }));
      //   },
      // }),

      // Disabled — using local mock data instead
      // saveLicense: builder.mutation<
      //   {
      //     data: { cloudLicenseKey: number; created: boolean };
      //     message: string;
      //   },
      //   { vendor: string; licenseData: LicenseFormData; endpointKeys: number[] }
      // >({
      //   queryFn: () =>
      //     new Promise<{
      //       data: {
      //         data: { cloudLicenseKey: number; created: boolean };
      //         message: string;
      //       };
      //     }>((resolve) => {
      //       setTimeout(() => {
      //         resolve({
      //           data: {
      //             data: { cloudLicenseKey: 9, created: true },
      //             message: 'License created',
      //           },
      //         });
      //       }, 4000);
      //     }),
      // }),

      // Disabled — using local mock data instead
      // runSimulation: builder.mutation<
      //   { message: string },
      //   {
      //     cloudLicenseKey: number;
      //     rulesetKey: number;
      //     endpointKeys: number[];
      //     vendorType: string;
      //   }
      // >({
      //   query: (payload) => ({
      //     url: '/api/license-intelligence/trigger-simulation',
      //     method: 'POST',
      //     data: payload,
      //   }),
      //   transformResponse: (response: { message?: string }) => ({
      //     message: response.message ?? 'Simulation started',
      //   }),
      // }),

      // Disabled — using local mock data instead
      // createLicenseExport: builder.mutation<
      //   unknown,
      //   CreateLicenseExportRequest
      // >({
      //   query: (payload) => ({
      //     url: '/api/license-intelligence/v1/sap/reports/exports/async',
      //     method: 'POST',
      //     isECMv6: true,
      //     data: payload,
      //   }),
      //   transformResponse: (response: {
      //     status?: number;
      //     data?: unknown;
      //     message?: string;
      //   }) => {
      //     if (response.status === 200) {
      //       return response.data;
      //     }
      //     return response;
      //   },
      // }),

      // getLicenseExportStatus: builder.query<LicenseExportStatusData, string>({
      //   query: (userId) => ({
      //     url: `/api/license-intelligence/v1/sap/reports/exports/status?userId=${userId}`,
      //     method: 'GET',
      //     isECMv6: true,
      //   }),
      //   transformResponse: (response: {
      //     status?: number;
      //     data?: LicenseExportStatusData;
      //   }): LicenseExportStatusData => {
      //     if (response.status === 200 && response.data) {
      //       return response.data;
      //     }
      //     return response as unknown as LicenseExportStatusData;
      //   },
      // }),

      // downloadLicenseExport: builder.query<Blob, string>({
      //   query: (exportId) => ({
      //     url: `/api/license-intelligence/v1/sap/reports/exports/${exportId}/download`,
      //     method: 'GET',
      //     isECMv6: true,
      //     config: { responseType: 'blob' },
      //   }),
      //   transformResponse: (response: unknown) => response as Blob,
      // }),
    }),
  });

export const {
  useGetLicenseStatusQuery,
  useLazySearchAccountRolesQuery,
  useLazySearchRoleAuthObjectsQuery,
  useGetFueInventoryKpiQuery,
  useGetInventoryFiltersQuery,
  useSearchIdentitiesQuery,
  useLazySearchIdentitiesQuery,
  useSearchIdentityAccountsQuery,
  useSearchIdentityRolesQuery,
  useRunSimulationMutation,
  useLazyDownloadLicenseExportQuery,
  useGetRulesetsQuery,
} = licenseManagementApiEndpoints;
